import { useRef } from "react";
import type { FacetOp, OpType } from "../facet-form-config";
import { OP_TYPES } from "../facet-form-config";
import FacetFormField, { INPUT_CLASS } from "./FacetFormField";
import FacetFormDropdown from "./FacetFormDropdown";
import NormalizeMappingEditor from "./NormalizeMappingEditor";
import { pruneNormalizeTransform } from "./normalize-transform-helpers";
import { useNormalizeElectionParties } from "../useNormalizeElectionParties";
import type { SourceListItem } from "../../../tars-hooks/useSourceList";
import type { SourceNode } from "../../sidebar/sources-types";

interface NormalizeOpFieldsProps {
    op: FacetOp;
    errors?: Record<string, string>;
    referenceTableOptions: string[];
    getNormalizeKeyValueOptions: (keyHint: string) => string[];
    optionsRefreshKey?: number;
    dataBankSources: SourceListItem[] | undefined;
    tarsNodes: Record<string, SourceNode>;
    onPatch: (updates: Partial<FacetOp>) => void;
    onTypeChange: (type: OpType) => void;
    onNormalizeKeyCommit?: (key: string) => void;
    isLoadingKeyValues?: boolean;
}

function dropdownWithCurrent(
    value: string,
    options: { value: string; label: string }[],
    emptyLabel: string
) {
    const trimmed = value.trim();
    const base = [{ value: "", label: emptyLabel }, ...options];
    if (trimmed && !options.some((opt) => opt.value === trimmed)) {
        base.push({ value: trimmed, label: trimmed });
    }
    return base;
}

export default function NormalizeOpFields({
    op,
    errors,
    referenceTableOptions,
    getNormalizeKeyValueOptions,
    optionsRefreshKey = 0,
    dataBankSources,
    tarsNodes,
    onPatch,
    onTypeChange,
    onNormalizeKeyCommit,
    isLoadingKeyValues = false,
}: NormalizeOpFieldsProps) {
    const skipNextBlurRef = useRef(false);
    const referenceTable = (op.reference_table ?? "").trim();
    const normalizeKey = (op.key ?? "").trim();
    const keyValueOptions = normalizeKey ? getNormalizeKeyValueOptions(normalizeKey) : [];

    const { data: partyNames = [], isLoading: partyNamesLoading } = useNormalizeElectionParties(
        referenceTable,
        dataBankSources,
        tarsNodes
    );

    const partyPlaceholder = !referenceTable
        ? "Select assembly first"
        : partyNamesLoading
          ? "Loading party names…"
          : partyNames.length
            ? "Select party name"
            : "No party names for this assembly";

    const commitKey = (rawKey: string) => {
        const trimmed = rawKey.trim();
        if (trimmed) onNormalizeKeyCommit?.(trimmed);
    };

    return (
        <div className="flex flex-col gap-4">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                <FacetFormField label="Type">
                    <FacetFormDropdown
                        value={op.type}
                        options={[...OP_TYPES]}
                        onChange={(v) => onTypeChange(v as OpType)}
                    />
                </FacetFormField>
                <FacetFormField label="Key" error={errors?.key}>
                    <input
                        type="text"
                        value={op.key ?? ""}
                        onChange={(e) => onPatch({ key: e.target.value })}
                        onBlur={(e) => {
                            if (skipNextBlurRef.current) {
                                skipNextBlurRef.current = false;
                                return;
                            }
                            commitKey(e.target.value);
                        }}
                        onPaste={(e) => {
                            const pasted = e.clipboardData.getData("text").trim();
                            if (!pasted) return;
                            e.preventDefault();
                            skipNextBlurRef.current = true;
                            onPatch({ key: pasted });
                            commitKey(pasted);
                        }}
                        placeholder="e.g. qr_party_support_in_2023"
                        className={INPUT_CLASS}
                    />
                </FacetFormField>
                <FacetFormField label="Reference Table">
                    <FacetFormDropdown
                        value={op.reference_table ?? ""}
                        options={dropdownWithCurrent(
                            op.reference_table ?? "",
                            referenceTableOptions.map((label) => ({ value: label, label })),
                            "Select assembly"
                        )}
                        onChange={(v) =>
                            onPatch({
                                reference_table: v,
                                transform: pruneNormalizeTransform(op.transform, keyValueOptions, []),
                            })
                        }
                    />
                </FacetFormField>
            </div>
            <FacetFormField label="Key Value → Party Name" error={errors?.transform}>
                {isLoadingKeyValues && (
                    <p className="mb-2 text-[11px] font-medium text-emerald-700">
                        Loading key values from facet…
                    </p>
                )}
                {!isLoadingKeyValues && normalizeKey && keyValueOptions.length === 0 && (
                    <p className="mb-2 text-[11px] font-medium text-slate-500">
                        No values returned for this key — check the key name and try again.
                    </p>
                )}
                <NormalizeMappingEditor
                    entries={op.transform ?? [{ key: "", value: "" }]}
                    keyValueOptions={keyValueOptions}
                    partyOptions={partyNames}
                    partyPlaceholder={partyPlaceholder}
                    optionsRefreshKey={optionsRefreshKey}
                    onChange={(transform) => onPatch({ transform })}
                />
            </FacetFormField>
        </div>
    );
}
